import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Order } from '../../../../backend/src/orders/order.entity';
import { NotificationService } from '../notification/notification.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { getCalendarDayDifference } from '../utils/order-deadline.util';

type OrderTab = 'Aktív' | 'Archív' | 'Mind';

interface OrderRowThumb {
  imageFileName: string | null;
  title: string;
  hasOverlay: boolean;
  overlay: string;
}

interface OrderRowVM {
  order: any;
  shopName: string;
  srcInitial: string;
  badgeClass: string;
  country: string;
  name: string;
  custom: boolean;
  city: string;
  idLabel: string;
  amtLabel: string;
  thumbs: OrderRowThumb[];
  days: number | null;
  shipToday: boolean;
  late: boolean;
  daysLabel: string;
  daysTitle: string;
  daysClass: string;
  stripClass: string;
  invLabel: string;
  invOn: boolean;
  invCancelled: boolean;
  lblLabel: string;
  lblOn: boolean;
  lateLabelPending: boolean;
  custMsg: string | null;
  teamMsgs: number;
  /** a felhasználó által még nem látott üzenetek — zöld jelzés */
  unreadMsgs: number;
  teamLabel: string;
  noMsgs: boolean;
  archived: boolean;
}

interface ManualField {
  key: string;
  label: string;
  ph: string;
  full: boolean;
}

interface ManualItem {
  componentId: number | null;
  name: string;
  sku: string;
  qty: number;
  price: string;
}

interface ManualSearchResult {
  id: number;
  name: string;
  sku: string;
  meta: string;
}

interface ManualDraft {
  type: 'Magán' | 'Céges';
  name: string;
  tax: string;
  email: string;
  country: string;
  zip: string;
  city: string;
  street: string;
  shipSame: boolean;
  shipName: string;
  shipCountry: string;
  shipZip: string;
  shipCity: string;
  shipStreet: string;
  items: ManualItem[];
  deadline: number;
  [key: string]: any;
}

const SHOP_DOT_COLORS: Record<string, string> = {
  'Etsy': '#B9834A',
  'Jump': '#5E7FA8',
  'Manuál': '#9A6E9E',
  'Shopify': '#5B8C6A',
};

const ALL_SHOPS = 'Mind';
const MANUAL_SHOP = 'Manuál';

interface OrdersListResponse {
  items: Order[];
  total: number;
  page: number;
  pageSize: number;
  todayCount?: number;
  lateCount?: number;
  shops?: string[];
}

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
})
export class OrdersComponent implements OnInit, OnDestroy {
  fetching = false;
  filterFormGroup!: FormGroup;
  pageSize = 50;
  pageIndex = 0;
  readonly pageSizeOptions: number[] = [25, 50, 100, 200];
  isArchivedPage = false;

  orders: any[] = [];
  totalOrders = 0;
  visibleRows: OrderRowVM[] = [];
  labelActionLoadingByOrderId: Record<number, boolean> = {};

  readonly tabs: OrderTab[] = ['Aktív', 'Archív', 'Mind'];

  // Bolt / határidő szűrők — szerver oldalon futnak, hogy a darabszámok és a
  // lapozó a TELJES találati halmazra vonatkozzanak, ne csak az aktuális oldalra.
  shopFilter = ALL_SHOPS;
  shopChips: string[] = [ALL_SHOPS];
  todayOnly = false;
  lateOnly = false;
  todayCount = 0;
  lateCount = 0;

  // Új manuál rendelés modal
  manualOpen = false;
  manual: ManualDraft = this.manualDefaults();
  manualBillFields: ManualField[] = [];
  readonly manualShipFields: ManualField[] = [
    { key: 'shipName', label: 'Név', ph: '', full: true },
    { key: 'shipCountry', label: 'Ország', ph: '', full: false },
    { key: 'shipZip', label: 'Irányítószám', ph: '', full: false },
    { key: 'shipCity', label: 'Város', ph: '', full: false },
    { key: 'shipStreet', label: 'Utca, házszám', ph: '', full: false },
  ];
  readonly manualDeadlines: number[] = [1, 3, 5, 7];
  manualSearchCtrl = new FormControl('');
  manualResults: ManualSearchResult[] = [];
  manualCreating = false;

  private readonly activeFilterStorageKey = 'ordersFilterGroup';
  private readonly archivedFilterStorageKey = 'ordersFilterGroupArchived';
  private manualSearchSub?: Subscription;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnInit() {
    this.isArchivedPage = this.route.snapshot.data?.['archivedPage'] === true;
    this.setPaging();
    this.createFilterFormGroup();

    const savedFilterGroup = localStorage.getItem(this.getFilterStorageKey());
    if (savedFilterGroup) {
      try {
        const filterValue = JSON.parse(savedFilterGroup);
        this.filterFormGroup.patchValue({
          ...filterValue,
          from: this.parseStoredDate(filterValue.from),
          to: this.parseStoredDate(filterValue.to)
        });
      } catch (e) {
        console.log('Error parsing saved filter group', e);
      }
    }

    // A régi UI "csak nem feladott" szűrőjének nincs felülete az új designban —
    // ne maradjon némán aktív egy korábbi mentésből.
    this.filterFormGroup.patchValue({ only_not_shipped: 0 }, { emitEvent: false });

    if (this.isArchivedPage) {
      this.filterFormGroup.patchValue({ archived: 'archived' });
    }

    this.manualSearchSub = this.manualSearchCtrl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((value) => this.fetchManualResults((value || '').trim()));

    this.fetchOrders();
  }

  ngOnDestroy() {
    this.manualSearchSub?.unsubscribe();
  }

  // --------------------------------------------------------------------------
  // Lekérés + szerver oldali szűrők
  // --------------------------------------------------------------------------

  setPaging() {
    const savedPageIndex = localStorage.getItem('orders:pageIndex');
    const savedPageSize = localStorage.getItem('orders:pageSize');
    if (savedPageSize !== null) {
      const parsedPageSize = parseInt(savedPageSize, 10);
      if (Number.isFinite(parsedPageSize) && this.pageSizeOptions.includes(parsedPageSize)) {
        this.pageSize = parsedPageSize;
      }
    }
    if (savedPageIndex !== null) {
      const parsedPageIndex = parseInt(savedPageIndex, 10);
      if (Number.isFinite(parsedPageIndex) && parsedPageIndex >= 0) {
        this.pageIndex = parsedPageIndex;
      }
    }
  }

  createFilterFormGroup() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 31);

    this.filterFormGroup = new FormGroup({
      from: new FormControl(oneMonthAgo),
      to: new FormControl(null),
      only_not_shipped: new FormControl(0),
      only_not_packaged: new FormControl(0),
      customer: new FormControl(''),
      sort_by: new FormControl('created_at'),
      archived: new FormControl(this.isArchivedPage ? 'archived' : 'active')
    });
  }

  getCurrentQueryFilter(): string {
    const params = new URLSearchParams();
    const from = this.filterFormGroup.get('from')?.value;
    const to = this.filterFormGroup.get('to')?.value;
    const onlyNotShipped = this.filterFormGroup.get('only_not_shipped')?.value;
    const onlyNotPackaged = this.filterFormGroup.get('only_not_packaged')?.value;
    const customer = this.filterFormGroup.get('customer')?.value;
    const sortBy = this.filterFormGroup.get('sort_by')?.value;
    const archived = this.isArchivedPage ? 'archived' : this.filterFormGroup.get('archived')?.value;
    const fromIso = this.toIsoDate(from);
    const toIso = this.toIsoDate(to);

    if (fromIso) {
      params.set('from', fromIso);
    }
    if (toIso) {
      params.set('to', toIso);
    }
    if (onlyNotShipped) {
      params.set('only_not_shipped', '1');
    }
    if (onlyNotPackaged) {
      params.set('only_not_packaged', '1');
    }
    if (customer) {
      params.set('search', customer);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
    }
    if (archived) {
      params.set('archived', archived);
    }
    if (this.shopFilter && this.shopFilter !== ALL_SHOPS) {
      params.set('shop', this.shopFilter);
    }
    if (this.todayOnly) {
      params.set('due', 'today');
    } else if (this.lateOnly) {
      params.set('due', 'late');
    }

    params.set('page', `${this.pageIndex}`);
    params.set('page_size', `${this.pageSize}`);

    return params.toString();
  }

  fetchOrders() {
    this.fetching = true;
    let queryFilter = '';
    try {
      queryFilter = this.getCurrentQueryFilter();
    } catch (error) {
      this.fetching = false;
      console.error('Error while building orders filter query', error);
      this.notificationService.show('A mentett rendelés-szűrő hibás volt, ezért alapértékekkel töltöttem vissza az oldalt.', 'warning');
      this.resetInvalidDateFilters();
      this.saveFilter();
      this.fetching = true;
      this.http.get<OrdersListResponse>(`orders?${this.getCurrentQueryFilter()}`).subscribe((response) => {
        this.handleOrdersResponse(response);
      }, () => {
        this.fetching = false;
      });
      return;
    }

    this.http.get<OrdersListResponse>(`orders?${queryFilter}`).subscribe((response) => {
      this.handleOrdersResponse(response);
    }, () => {
      this.fetching = false;
    });
  }

  private handleOrdersResponse(response: OrdersListResponse) {
    this.totalOrders = response?.total || 0;
    this.orders = response?.items || [];
    this.todayCount = Number(response?.todayCount) || 0;
    this.lateCount = Number(response?.lateCount) || 0;

    const shops = (response?.shops || []).filter((shop) => !!shop);
    this.shopChips = [ALL_SHOPS, ...shops];
    if (!this.shopChips.includes(this.shopFilter)) {
      this.shopFilter = ALL_SHOPS;
    }

    if (!this.orders.length && this.totalOrders > 0 && this.pageIndex > 0) {
      this.pageIndex = Math.max(Math.ceil(this.totalOrders / this.pageSize) - 1, 0);
      localStorage.setItem('orders:pageIndex', `${this.pageIndex}`);
      this.fetchOrders();
      return;
    }
    this.visibleRows = this.orders.map((order) => this.buildRow(order));
    this.fetching = false;
  }

  onFiltersChanged(resetPage: boolean = true) {
    if (resetPage) {
      this.resetToFirstPage();
    }
    this.saveFilter();
    this.fetchOrders();
  }

  toggleUnpacked() {
    const control = this.filterFormGroup.get('only_not_packaged');
    control?.setValue(control?.value ? 0 : 1);
    this.onFiltersChanged();
  }

  get unpackedOnly(): boolean {
    return !!this.filterFormGroup?.get('only_not_packaged')?.value;
  }

  get currentTab(): OrderTab {
    if (this.isArchivedPage) {
      return 'Archív';
    }
    const archived = this.filterFormGroup?.get('archived')?.value;
    if (archived === 'archived') return 'Archív';
    if (archived === 'all') return 'Mind';
    return 'Aktív';
  }

  setTab(tab: OrderTab) {
    if (this.isArchivedPage) {
      return;
    }
    const mode = tab === 'Archív' ? 'archived' : (tab === 'Mind' ? 'all' : 'active');
    this.filterFormGroup.patchValue({ archived: mode });
    this.onFiltersChanged();
  }

  saveFilter() {
    localStorage.setItem(this.getFilterStorageKey(), JSON.stringify(this.filterFormGroup.value));
  }

  dateInputValue(controlName: 'from' | 'to'): string {
    const value = this.filterFormGroup?.get(controlName)?.value;
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return '';
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  onDateInput(controlName: 'from' | 'to', event: Event) {
    const raw = (event.target as HTMLInputElement)?.value || '';
    let parsed: Date | null = null;
    if (raw) {
      const candidate = new Date(`${raw}T00:00:00`);
      parsed = Number.isNaN(candidate.getTime()) ? null : candidate;
    }
    this.filterFormGroup.patchValue({ [controlName]: parsed });
    this.onFiltersChanged();
  }

  // --------------------------------------------------------------------------
  // Kliens oldali szűrők (bolt, mai feladások, késői feladások)
  // --------------------------------------------------------------------------

  setShopFilter(name: string) {
    if (this.shopFilter === name) {
      return;
    }
    this.shopFilter = name;
    this.onFiltersChanged();
  }

  /** A „ma esedékes” és a „már késik” egymást kizárja — egyszerre nem lehet igaz. */
  toggleToday() {
    this.todayOnly = !this.todayOnly;
    if (this.todayOnly) {
      this.lateOnly = false;
    }
    this.onFiltersChanged();
  }

  toggleLate() {
    this.lateOnly = !this.lateOnly;
    if (this.lateOnly) {
      this.todayOnly = false;
    }
    this.onFiltersChanged();
  }

  getShopDotColor(name: string): string {
    return SHOP_DOT_COLORS[name] || '#9AA098';
  }

  private rebuildRows() {
    this.visibleRows = this.orders.map((order) => this.buildRow(order));
  }

  // --------------------------------------------------------------------------
  // Sor view-model a valós order adatmodellből
  // --------------------------------------------------------------------------

  private buildRow(order: any): OrderRowVM {
    const days = getCalendarDayDifference(order?.shipping_expected_at);
    const late = days !== null && days < 0;
    const shipToday = days === 0;
    const shopName = this.getMerchantName(order);
    const hasInvoice = this.hasInvoice(order);
    const invCancelled = order?.billingo_invoice_cancelled === 1;
    const hasLabel = this.hasLabel(order);
    const custMsg = typeof order?.customer_message === 'string' && order.customer_message.trim().length > 0
      ? order.customer_message.trim()
      : null;
    const teamMsgs = Number(order?.teamMessageCount ?? order?.team_message_count ?? 0) || 0;
    const unreadMsgs = Number(order?.unreadMessageCount ?? 0) || 0;
    const lateLabelPending = late && hasLabel;
    const products: any[] = Array.isArray(order?.orderProducts) ? order.orderProducts : [];

    let daysLabel = '–';
    let daysTitle = 'Nincs feladási határidő';
    let daysClass = 'ord-days--grey';
    let stripClass = 'ord-row--strip-grey';
    if (days !== null) {
      if (late) {
        daysLabel = 'Késik';
        daysTitle = `${Math.abs(days)} napja lejárt a szállítási határidő`;
        daysClass = 'ord-days--red';
        stripClass = 'ord-row--strip-red';
      } else if (shipToday) {
        daysLabel = 'Ma';
        daysTitle = 'Ma kell feladni';
        daysClass = 'ord-days--yellow';
        stripClass = 'ord-row--strip-yellow';
      } else {
        daysLabel = `${days}`;
        daysTitle = `${days} nap van hátra`;
        daysClass = 'ord-days--green';
        stripClass = 'ord-row--strip-green';
      }
    }

    return {
      order,
      shopName,
      srcInitial: (shopName || '?').charAt(0).toUpperCase(),
      badgeClass: this.getMerchantBadgeClass(order),
      country: order?.country_iso || order?.shipping_country || '–',
      name: order?.customer || 'Ismeretlen vevő',
      custom: this.isCustomOrder(order),
      city: order?.shipping_city || order?.address || '',
      idLabel: `#${order?.receipt_id}`,
      amtLabel: `${Math.round(Number(order?.price) || 0)} ${order?.currency_code || ''}`.trim(),
      thumbs: products.slice(0, 5).map((product, index) => ({
        imageFileName: this.getProductImageFileName(product),
        title: this.getProductTitle(product),
        hasOverlay: index === 4 && products.length > 5,
        overlay: `+${products.length - 5}`,
      })),
      days,
      shipToday,
      late,
      daysLabel,
      daysTitle,
      daysClass,
      stripClass,
      invLabel: invCancelled ? 'Sztornó' : (hasInvoice ? 'Van' : 'Nincs'),
      invOn: hasInvoice && !invCancelled,
      invCancelled,
      lblLabel: hasLabel ? 'Van' : 'Nincs',
      lblOn: hasLabel,
      lateLabelPending,
      custMsg,
      teamMsgs,
      unreadMsgs,
      teamLabel: `Munkatársi üzenőfal · ${teamMsgs} bejegyzés`,
      noMsgs: !custMsg && teamMsgs <= 0 && !lateLabelPending,
      archived: order?.is_archived === 1,
    };
  }

  getMerchantName(order: any): string {
    if (this.isCustomOrder(order)) {
      return MANUAL_SHOP;
    }
    if (this.isJumpOrder(order)) {
      return 'Jump';
    }
    if (order?.source_key === 'medusa') {
      return 'Webshop';
    }
    return order?.merchant?.name || this.resolveMerchantName(order?.merchant_id);
  }

  getMerchantBadgeClass(order: any): string {
    if (this.isJumpOrder(order) || order?.source_key === 'medusa') {
      return 'ord-src--jump';
    }
    if (this.isCustomOrder(order)) {
      return 'ord-src--manual';
    }
    switch (order?.merchant_id) {
      case 1:
        return 'ord-src--etsy';
      default:
        return 'ord-src--default';
    }
  }

  private isJumpOrder(order: any): boolean {
    return order?.merchant_id === 1 && order?.etsy_shop_id === 2;
  }

  private isCustomOrder(order: any): boolean {
    return order?.source_key === 'manual' || order?.is_manual === 1;
  }

  hasInvoice(order: any): boolean {
    return !!(order?.billingo_invoice_id || order?.billingo_invoice_download_url);
  }

  hasLabel(order: any): boolean {
    return !!(order?.shippypro_label_url || order?.tracking_code);
  }

  getProductImageFileName(product: any): string | null {
    const imageFile = product?.component?.image_file;
    return typeof imageFile === 'string' && imageFile.trim().length > 0 ? imageFile : null;
  }

  getProductTitle(product: any): string {
    return this.asDisplayText(product?.name)
      || this.asDisplayText(product?.component?.name)
      || this.asDisplayText(product?.sku)
      || 'Ismeretlen termék';
  }

  // --------------------------------------------------------------------------
  // Számla / címke műveletek
  // --------------------------------------------------------------------------

  openInvoice(order: any) {
    if (!this.hasInvoice(order)) {
      this.notificationService.show('Ehhez a rendeléshez még nincs számla.', 'warning');
      return;
    }

    this.http.get<{download_url: string | null; document_url?: string | null; provider?: string}>(`orders/${order.id}/invoice/download`).subscribe((res) => {
      if (res?.download_url) {
        window.open(res.download_url, '_blank');
        return;
      }
      // Az Okos Könyvelőm nem ad bejelentkezés nélkül megnyitható számlaképet: a PDF-et a
      // backend tölti le a kulccsal, mi pedig blobként nyitjuk meg (mint a szállítólevélnél).
      if (res?.document_url) {
        this.openInvoiceDocument(order?.id);
        return;
      }
      this.notificationService.show('Nincs letölthető számla URL.', 'warning');
    }, (err) => {
      this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  private openInvoiceDocument(orderId: number) {
    const previewWindow = window.open('', '_blank');
    this.http.get(`orders/${orderId}/invoice/document`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
      error: (err) => {
        previewWindow?.close();
        this.notificationService.show(`Számla letöltés sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      },
    });
  }

  openLabel(order: any) {
    if (order?.shippypro_label_url) {
      this.openLabelDocument(order?.id);
      return;
    }

    if (order?.tracking_code) {
      this.notificationService.show(`Tracking kód: ${order.tracking_code}`, 'info', 2500);
      return;
    }

    this.confirmDialog.open({
      title: 'Címke generálása',
      message: 'Ehhez a rendeléshez még nincs címke. Legeneráljuk most?',
      details: 'Sikeres generálás után a címke új lapon nyílik meg.',
      confirmLabel: 'Címke generálása',
      tone: 'primary',
      icon: 'fa-solid fa-tag'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.generateLabelForOrder(order);
    });
  }

  isLabelActionLoading(order: any): boolean {
    const orderId = Number(order?.id || 0);
    return orderId > 0 ? !!this.labelActionLoadingByOrderId[orderId] : false;
  }

  private generateLabelForOrder(order: any) {
    const orderId = Number(order?.id || 0);
    if (orderId <= 0 || this.labelActionLoadingByOrderId[orderId]) {
      return;
    }

    this.labelActionLoadingByOrderId[orderId] = true;
    this.http.post(`orders/${orderId}/generate-label`, { regenerate: false }).subscribe((updatedOrder: any) => {
      this.labelActionLoadingByOrderId[orderId] = false;
      Object.assign(order, updatedOrder || {});
      this.rebuildRows();
      this.notificationService.show('Címke generálva.', 'success');
      if (updatedOrder?.shippypro_label_url) {
        this.openLabelDocument(orderId);
        return;
      }
      if (updatedOrder?.tracking_code) {
        this.notificationService.show(`Tracking kód: ${updatedOrder.tracking_code}`, 'info', 2500);
      }
    }, (err) => {
      this.labelActionLoadingByOrderId[orderId] = false;
      this.notificationService.show(`Címkegenerálás sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  private openLabelDocument(orderId: number) {
    const resolvedOrderId = Number(orderId || 0);
    if (resolvedOrderId <= 0) {
      this.notificationService.show('A címke megnyitásához hiányzik a rendelés azonosítója.', 'warning');
      return;
    }

    const previewWindow = window.open('', '_blank');

    this.http.get(`orders/${resolvedOrderId}/label/document`, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        if (previewWindow) {
          previewWindow.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
      error: (err) => {
        if (previewWindow) {
          previewWindow.close();
        }
        this.notificationService.show(`Címke megnyitása sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      }
    });
  }

  // --------------------------------------------------------------------------
  // Archiválás / visszaállítás
  // --------------------------------------------------------------------------

  toggleArchive(row: OrderRowVM) {
    if (row.archived) {
      this.restoreOrder(row.order);
    } else {
      this.archiveOrder(row.order);
    }
  }

  archiveOrder(order: any) {
    this.confirmDialog.open({
      title: 'Rendelés archiválása',
      message: 'Biztosan archiválod ezt a rendelést?',
      details: 'Archiválás után a rendelés átkerül az archív nézetbe, és a kiállított számla ALAPÉRTELMEZÉS szerint sztornózódik. A sztornó a NAV felé is jelentődik, és nem vonható vissza — ha a számlának érvényben kell maradnia, vedd ki a pipát.',
      confirmLabel: 'Archiválás',
      tone: 'warning',
      icon: 'fa-solid fa-box-archive',
      checkboxLabel: 'Készletből is vonjuk le most',
      secondaryCheckboxLabel: 'A kiállított számlát sztornózzuk',
      secondaryCheckboxDefault: true
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.patch(`orders/${order.id}/archive`, {
        deduct_stock: result.checkboxChecked,
        cancel_invoice: result.secondaryCheckboxChecked
      }).subscribe(() => {
        this.notificationService.show('Rendelés archiválva.', 'success');
        this.fetchOrders();
      }, (err) => {
        this.notificationService.show(`Archiválás sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      });
    });
  }

  restoreOrder(order: any) {
    this.confirmDialog.open({
      title: 'Rendelés visszaállítása',
      message: 'Biztosan visszaállítod aktívra?',
      details: 'Visszaállítás után a rendelés az aktív nézetbe kerül. Ha korábban levontuk a készletből, most vissza is helyezhetjük.',
      confirmLabel: 'Visszaállítás',
      tone: 'success',
      icon: 'fa-solid fa-box-open',
      checkboxLabel: 'Készletbe helyezzük vissza a terméket'
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.patch(`orders/${order.id}/restore`, { restore_stock: result.checkboxChecked }).subscribe(() => {
        this.notificationService.show('Rendelés visszaállítva.', 'success');
        this.fetchOrders();
      }, (err) => {
        this.notificationService.show(`Visszaállítás sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
      });
    });
  }

  // --------------------------------------------------------------------------
  // Lábléc + lapozás
  // --------------------------------------------------------------------------

  get footerLabel(): string {
    return `${this.totalOrders} rendelés · ${this.currentTab.toLowerCase()} nézet`;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalOrders / this.pageSize));
  }

  get pageRangeLabel(): string {
    if (!this.totalOrders) {
      return '0 / 0';
    }
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageSize, this.totalOrders);
    return `${start}–${end} / ${this.totalOrders}`;
  }

  get isLastPage(): boolean {
    return this.pageIndex >= this.totalPages - 1;
  }

  goToPage(pageIndex: number) {
    const clamped = Math.min(Math.max(0, pageIndex), this.totalPages - 1);
    if (clamped === this.pageIndex) {
      return;
    }
    this.pageIndex = clamped;
    localStorage.setItem('orders:pageIndex', `${this.pageIndex}`);
    this.fetchOrders();
  }

  setPageSize(size: number | string) {
    const parsed = Number(size);
    if (!this.pageSizeOptions.includes(parsed) || parsed === this.pageSize) {
      return;
    }
    this.pageSize = parsed;
    this.pageIndex = 0;
    localStorage.setItem('orders:pageSize', `${this.pageSize}`);
    localStorage.setItem('orders:pageIndex', '0');
    this.fetchOrders();
  }

  trackByOrderId(_: number, row: OrderRowVM) {
    return row?.order?.id;
  }

  trackByIndex(index: number) {
    return index;
  }

  trackByFieldKey(_: number, field: ManualField) {
    return field.key;
  }

  // --------------------------------------------------------------------------
  // Új manuál rendelés modal
  // --------------------------------------------------------------------------

  private manualDefaults(): ManualDraft {
    return {
      type: 'Magán', name: '', tax: '', email: '', country: '', zip: '', city: '', street: '',
      shipSame: true, shipName: '', shipCountry: '', shipZip: '', shipCity: '', shipStreet: '',
      items: [], deadline: 5,
    };
  }

  openManual() {
    this.manual = this.manualDefaults();
    this.rebuildManualBillFields();
    this.manualResults = [];
    this.manualSearchCtrl.setValue('', { emitEvent: false });
    this.manualOpen = true;
  }

  closeManual() {
    this.manualOpen = false;
  }

  setManualType(type: 'Magán' | 'Céges') {
    this.manual.type = type;
    this.rebuildManualBillFields();
  }

  private rebuildManualBillFields() {
    const company = this.manual.type === 'Céges';
    this.manualBillFields = [
      { key: 'name', label: company ? 'Cégnév' : 'Név', ph: company ? 'BGFox Kft.' : 'Kovács Anna', full: !company },
      ...(company ? [{ key: 'tax', label: 'Adószám', ph: 'HU12345678', full: false }] : []),
      { key: 'email', label: 'Email', ph: 'nev@email.com', full: company },
      { key: 'country', label: 'Ország', ph: 'DE', full: false },
      { key: 'zip', label: 'Irányítószám', ph: '10115', full: false },
      { key: 'city', label: 'Város', ph: 'Berlin', full: false },
      { key: 'street', label: 'Utca, házszám', ph: 'Musterstraße 12.', full: false },
    ];
  }

  toggleManualShipSame() {
    this.manual.shipSame = !this.manual.shipSame;
  }

  private fetchManualResults(query: string) {
    if (!this.manualOpen || !query) {
      this.manualResults = [];
      return;
    }
    this.http.get<any[]>(`components?filter=${encodeURIComponent(query)}`).subscribe((components) => {
      const addedSkus = new Set(this.manual.items.map((item) => item.sku));
      this.manualResults = (components || [])
        .filter((component) => component?.for_sale === 1 && !addedSkus.has(component?.sku))
        .slice(0, 5)
        .map((component) => {
          const stockPart = component?.stock !== undefined && component?.stock !== null
            ? ` · készlet: ${component.stock} db`
            : '';
          return {
            id: component.id,
            name: component.name,
            sku: component.sku,
            meta: `${component.sku || '—'}${stockPart}`,
          };
        });
    }, () => {
      this.manualResults = [];
    });
  }

  addManualItem(result: ManualSearchResult) {
    this.manual.items = [
      ...this.manual.items,
      { componentId: result.id, name: result.name, sku: result.sku, qty: 1, price: '' },
    ];
    this.manualResults = [];
    this.manualSearchCtrl.setValue('', { emitEvent: false });
  }

  removeManualItem(index: number) {
    this.manual.items = this.manual.items.filter((_, i) => i !== index);
  }

  setManualQty(index: number, value: string | number) {
    const qty = Math.max(1, parseInt(`${value}`, 10) || 1);
    this.manual.items = this.manual.items.map((item, i) => i === index ? { ...item, qty } : item);
  }

  setManualPrice(index: number, value: string) {
    this.manual.items = this.manual.items.map((item, i) => i === index ? { ...item, price: value } : item);
  }

  setManualDeadline(days: number) {
    this.manual.deadline = days;
  }

  manualLineSum(item: ManualItem): string {
    return `${(item.qty * (parseFloat(item.price) || 0)).toFixed(2)} EUR`;
  }

  get manualTotal(): string {
    const total = this.manual.items.reduce((sum, item) => sum + item.qty * (parseFloat(item.price) || 0), 0);
    return `${total.toFixed(2)} EUR`;
  }

  get manualCanCreate(): boolean {
    return !!this.manual.name.trim()
      && this.manual.items.length > 0
      && this.manual.items.every((item) => parseFloat(item.price) > 0);
  }

  createManualOrder() {
    if (!this.manualCanCreate || this.manualCreating) {
      return;
    }
    const m = this.manual;
    const payload = {
      customer_type: m.type === 'Céges' ? 'company' : 'private',
      name: m.name.trim(),
      tax_number: m.type === 'Céges' ? m.tax.trim() : null,
      email: m.email.trim(),
      country: m.country.trim().toUpperCase(),
      zip: m.zip.trim(),
      city: m.city.trim(),
      street: m.street.trim(),
      shipping_same_as_billing: m.shipSame,
      shipping: m.shipSame ? null : {
        name: m.shipName.trim(),
        country: m.shipCountry.trim().toUpperCase(),
        zip: m.shipZip.trim(),
        city: m.shipCity.trim(),
        street: m.shipStreet.trim(),
      },
      deadline_days: m.deadline,
      items: m.items.map((item) => ({
        component_id: item.componentId,
        sku: item.sku,
        name: item.name,
        quantity: item.qty,
        unit_price_eur: parseFloat(item.price) || 0,
      })),
    };

    this.manualCreating = true;
    this.http.post(`orders/manual`, payload).subscribe(() => {
      this.manualCreating = false;
      this.manualOpen = false;
      this.notificationService.show('Manuál rendelés létrehozva.', 'success');
      this.fetchOrders();
    }, (err) => {
      this.manualCreating = false;
      this.notificationService.show(`Rendelés létrehozása sikertelen! Részletek: ${err.error?.message || err.message}`, 'danger');
    });
  }

  // --------------------------------------------------------------------------
  // Segédek
  // --------------------------------------------------------------------------

  private resetToFirstPage() {
    this.pageIndex = 0;
    localStorage.setItem('orders:pageIndex', '0');
  }

  private asDisplayText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private parseStoredDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    const parsedDate = value instanceof Date ? value : new Date(value as string);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * A dátumszűrő paramétere naptári nap (YYYY-MM-DD), NEM UTC időbélyeg:
   * a `toISOString()` a lokális éjfélt UTC-re tolja, így a szűrő egy napot
   * csúszna (pl. CEST-ben a 07-14 „2026-07-13T22:00Z”-ként ment ki).
   */
  private toIsoDate(value: unknown): string | null {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  private resetInvalidDateFilters() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 31);
    this.filterFormGroup.patchValue({
      from: oneMonthAgo,
      to: null
    });
  }

  private resolveMerchantName(merchantId: number): string {
    switch (merchantId) {
      case 1:
        return 'Etsy';
      case 2:
        return 'Shopify';
      case 3:
        return 'WooCommerce';
      case 4:
        return 'Philibert';
      default:
        return 'Shop';
    }
  }

  private getFilterStorageKey(): string {
    return this.isArchivedPage ? this.archivedFilterStorageKey : this.activeFilterStorageKey;
  }
}
